import { useCallback, useMemo, useState } from 'react'
import { format, startOfDay } from 'date-fns'
import { User, Users } from 'lucide-react'
import type { CalendarDayData, ScheduleDaySummary } from '@/shared/domain'
import { MAX_BOOKING_DURATION_DAYS } from '@/shared/domain/bookings'
import {
  StyledCalendar,
  type StyledCalendarDayDecoration,
  type StyledCalendarLegendItem,
} from './styled-calendar'
import { resolveDateRangeClick, type DateRangeSelection } from './date-range-click'

const AVAILABILITY_FILL: Record<
  CalendarDayData['availability'],
  string | undefined
> = {
  available: 'bg-sage-soft hover:bg-sage-soft',
  limited: 'bg-amber-soft hover:bg-amber-soft',
  full: 'bg-destructive/15 hover:bg-destructive/15',
  none: undefined,
}

const CAPACITY_LEGEND: StyledCalendarLegendItem[] = [
  { type: 'swatch', color: 'bg-sage-soft', label: 'Available' },
  { type: 'swatch', color: 'bg-amber-soft', label: 'Limited' },
  { type: 'swatch', color: 'bg-destructive/15', label: 'Full' },
]

const ATTENTION_LEGEND_ITEM: StyledCalendarLegendItem = {
  type: 'dot',
  color: 'bg-destructive',
  label: 'Needs attention',
}

const HUNTS_LEGEND_ITEM: StyledCalendarLegendItem = { type: 'dot', color: 'bg-blaze', label: 'Hunts' }
const EVENTS_LEGEND_ITEM: StyledCalendarLegendItem = { type: 'dot', color: 'bg-steel', label: 'Events' }
const BOOKINGS_LEGEND_ITEM: StyledCalendarLegendItem = { type: 'dot', color: 'bg-sage', label: 'Bookings' }
const DRAFT_BOOKINGS_LEGEND_ITEM: StyledCalendarLegendItem = {
  type: 'dot',
  color: 'border-sage',
  label: 'Draft bookings',
  hollow: true,
}
const PUBLIC_EVENTS_LEGEND_ITEM: StyledCalendarLegendItem = {
  type: 'dot',
  color: 'bg-clay',
  label: 'Public events',
}

const NO_SCHEDULE_DAYS: Record<string, ScheduleDaySummary> = {}

export type BookingDateRangeValue = {
  start: Date | null
  end: Date | null
  onChange: (range: DateRangeSelection) => void
}

type SingleDateSelection = {
  selectedDate: Date | null
  onSelectDate: (date: Date) => void
  range?: never
}

type DateRangeSelectionMode = {
  range: BookingDateRangeValue
  selectedDate?: never
  onSelectDate?: never
}

export type BookingDateSelectorProps = (SingleDateSelection | DateRangeSelectionMode) & {
  calendarDays?: CalendarDayData[]
  scheduleDays?: Record<string, ScheduleDaySummary>
  publicEventDays?: string[]
  isLoading?: boolean
  minDate?: Date
}

export function BookingDateSelector({
  selectedDate,
  onSelectDate,
  range,
  calendarDays,
  scheduleDays = NO_SCHEDULE_DAYS,
  publicEventDays,
  isLoading = false,
  minDate,
}: BookingDateSelectorProps) {
  const [focusedMonth, setFocusedMonth] = useState<Date>(
    () => selectedDate ?? range?.start ?? startOfDay(new Date()),
  )
  const [rangeAnchor, setRangeAnchor] = useState<Date | null>(null)

  const dayByDate = useMemo(() => {
    const map = new Map<string, CalendarDayData>()
    for (const day of calendarDays ?? []) map.set(day.date, day)
    return map
  }, [calendarDays])

  const showCapacity = useMemo(
    () => (calendarDays ?? []).some((day) => day.totalCapacity !== undefined),
    [calendarDays],
  )
  const showAttention = useMemo(
    () => (calendarDays ?? []).some((day) => day.hasAttention),
    [calendarDays],
  )

  const publicEventDaySet = useMemo(() => new Set(publicEventDays ?? []), [publicEventDays])

  const dotLegend = useMemo<StyledCalendarLegendItem[]>(() => {
    const summaries = Object.values(scheduleDays)
    const days = calendarDays ?? []
    const items: StyledCalendarLegendItem[] = []
    if (summaries.some((summary) => summary.hasHunts)) items.push(HUNTS_LEGEND_ITEM)
    if (summaries.some((summary) => summary.hasEvents)) items.push(EVENTS_LEGEND_ITEM)
    if (days.some((day) => day.hasConfirmedBookings)) items.push(BOOKINGS_LEGEND_ITEM)
    if (days.some((day) => day.hasDraftBookings)) items.push(DRAFT_BOOKINGS_LEGEND_ITEM)
    if (publicEventDaySet.size > 0) items.push(PUBLIC_EVENTS_LEGEND_ITEM)
    return items
  }, [scheduleDays, calendarDays, publicEventDaySet])

  const legend = useMemo<StyledCalendarLegendItem[] | undefined>(() => {
    const items: StyledCalendarLegendItem[] = []
    if (showCapacity) items.push(...CAPACITY_LEGEND)
    items.push(...dotLegend)
    if (showAttention) items.push(ATTENTION_LEGEND_ITEM)
    return items.length > 0 ? items : undefined
  }, [showCapacity, dotLegend, showAttention])

  const resolveDay = useCallback(
    (date: Date): StyledCalendarDayDecoration | undefined => {
      const dateKey = format(date, 'yyyy-MM-dd')
      const day = dayByDate.get(dateKey)
      const summary = scheduleDays[dateKey]
      const dots: NonNullable<StyledCalendarDayDecoration['dots']> = []
      if (summary?.hasHunts) dots.push({ color: 'bg-blaze', ariaLabel: 'Has hunts' })
      if (summary?.hasEvents) dots.push({ color: 'bg-steel', ariaLabel: 'Has events' })
      if (day?.hasConfirmedBookings) dots.push({ color: 'bg-sage', ariaLabel: 'Has confirmed bookings' })
      if (day?.hasDraftBookings) {
        dots.push({ color: 'border-sage', ariaLabel: 'Has draft bookings', hollow: true })
      }
      if (publicEventDaySet.has(dateKey)) dots.push({ color: 'bg-clay', ariaLabel: 'Has public events' })
      const decoration: StyledCalendarDayDecoration = {}
      if (dots.length > 0) decoration.dots = dots
      if (!day) return dots.length > 0 ? decoration : undefined
      const fill = AVAILABILITY_FILL[day.availability]
      if (fill) decoration.fill = fill
      if (day.badgeValue > 0) {
        const HunterIcon = day.badgeValue === 1 ? User : Users
        decoration.caption = (
          <span className="flex items-center gap-0.5">
            <HunterIcon aria-hidden className="size-2.5" />
            <span className="tabular-nums">{day.badgeValue}</span>
          </span>
        )
      }
      if (day.hasAttention) {
        decoration.corner = {
          color: 'bg-destructive',
          ariaLabel: 'Needs attention',
        }
      }
      return Object.keys(decoration).length > 0 ? decoration : undefined
    },
    [dayByDate, scheduleDays, publicEventDaySet],
  )

  const handleDateSelect = useCallback(
    (date: Date) => {
      const day = startOfDay(date)
      if (day.getMonth() !== focusedMonth.getMonth() || day.getFullYear() !== focusedMonth.getFullYear()) {
        setFocusedMonth(day)
      }
      if (!range) {
        onSelectDate?.(day)
        return
      }
      const next = resolveDateRangeClick(
        { start: range.start, end: range.end, anchor: rangeAnchor },
        day,
        MAX_BOOKING_DURATION_DAYS,
      )
      setRangeAnchor(next.end === null ? next.start : null)
      range.onChange(next)
    },
    [focusedMonth, onSelectDate, range, rangeAnchor],
  )

  const rangeStart = range?.start ?? null
  const rangeEnd = range ? (range.end ?? range.start) : null

  return (
    <StyledCalendar
      selectedDate={range ? null : selectedDate}
      focusedMonth={focusedMonth}
      isLoading={isLoading}
      minDate={minDate}
      onDateSelect={handleDateSelect}
      onMonthChange={setFocusedMonth}
      resolveDay={resolveDay}
      legend={legend}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
    />
  )
}
